import type {
    DateGranularity,
    DisplayAs,
    ExcelChartLayout,
    ExcelChartSettings,
    ExcelColumnAxisOptions,
    ExcelMeasureOptions,
    ExcelMeasureScale,
    ExcelNumberFormat,
    ExcelRowLabelOptions,
} from "../types";
import { normalizeComboChartSettings } from "./chartCombo";

export function makeDefaultExcelChartSettings(): ExcelChartSettings {
    return {
        showTitle: true,
        title: "",
        showLegend: true,
        legendPosition: "bottom",
        showDataLabels: false,
        categoryAxisTitle: "",
        valueAxisTitle: "",
        valueNumberFormat: "general",
        valueAxisMinimum: null,
        valueAxisMaximum: null,
        valueAxisInterval: null,
        showMajorGridlines: false,
        palette: "office",
        widthColumns: null,
        heightRows: null,
        gapWidth: 150,
        showLineMarkers: false,
        lineWidth: "automatic",
    };
}

export function normalizeExcelChartLayout(chart?: Partial<ExcelChartLayout> | null): ExcelChartLayout {
    const dataOrientation = chart?.dataOrientation === "rowsAsCategories" || chart?.dataOrientation === "rowsAsSeries"
        ? chart.dataOrientation
        : undefined;
    return {
        enabled: chart?.enabled ?? false,
        type: chart?.type ?? "none",
        ...(dataOrientation ? { dataOrientation } : {}),
        seriesRowLabels: Array.isArray(chart?.seriesRowLabels) ? chart.seriesRowLabels : [],
        dataLabels: chart?.dataLabels ?? true,
        settings: { ...makeDefaultExcelChartSettings(), ...(chart?.settings || {}) },
        // Only written when present, so a chart saved before combo charts existed round-trips
        // to exactly the same JSON.
        ...(chart?.mode === "combo" || chart?.mode === "single" ? { mode: chart.mode } : {}),
        ...(chart?.combo ? { combo: normalizeComboChartSettings(chart.combo) } : {}),
    };
}

function excelDefaultNumberFormat(displayAs: DisplayAs): ExcelNumberFormat {
    return displayAs === "percentage" || displayAs === "otp" ? "percent2" : "accountingInt";
}

function excelDefaultScale(displayAs: DisplayAs): ExcelMeasureScale {
    return displayAs === "percentage" || displayAs === "otp" ? "divide100" : "raw";
}

export function makeDefaultExcelRowLabel(label: string): ExcelRowLabelOptions {
    return { displayLabel: label, valueOrder: "asReturned", explicitOrder: [], labelMap: {}, showTotalRow: true, totalLabel: "Total", totalPosition: "bottom" };
}

export function defaultColumnAxisOptions(granularity: DateGranularity): ExcelColumnAxisOptions {
    return { granularity, headerFormat: "MMM", fillMissingPeriods: true, showTotalColumn: true, totalAggregation: "sum" };
}

export function makeDefaultExcelMeasure(displayAs: DisplayAs): ExcelMeasureOptions {
    return { numberFormat: excelDefaultNumberFormat(displayAs), scale: excelDefaultScale(displayAs) };
}
